import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  List,
  Typography,
  alpha,
} from '@mui/material';
import { useNavigate } from 'react-router';
import {
  BusinessOutlined,
  EditOutlined,
  FestivalOutlined,
  GroupsOutlined,
  SwapHorizOutlined,
  SettingsOutlined,
} from '@mui/icons-material';
import { Fragment, type ReactNode, useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router';

import { useTranslations } from '@/i18n/use-translations';
import { useActiveOrganizationStore } from '@/state/stores/use-organization-store';
import { useGetOrganizationQuery } from '@/state/queries/organizations/use-get-organization.ts';
import { usePermissionsQuery } from '@/state/queries/permissions/use-permissions.ts';
import { ORGANIZATION_PERMISSIONS } from '@/enums/permissions.enum';

import UserOrganizations from '@/components/organizations/user-organizations';
import EditActiveOrganization from '@/components/organizations/edit-active-organization';
import ActiveOrganizationMenuItem from '@/components/organizations/active-organization-menu/menu-item.tsx';
import { getPrimaryOrganizationSubscriptionInstance } from '@/helpers/organization-subscriptions';
import {
  bookedMainSubscriptionSummaryEntries,
  normalizeSubscriptionProductKey,
  subscriptionCategoryKey,
  type BookedMainPlanSummaryEntry,
} from '@/helpers/organization-plan-catalog';
import type { SubscriptionCategory } from '@/types/dtos/transactions/subscription-plan.dto';
import { resolveTranslatedProductField } from '@/helpers/resolve-translated-product-field';
import type { OrganizationDto } from '@/types/dtos/organizations/organization.dto';
const MENU_TYPE = {
  content: 'content',
  events: 'events',
  team: 'team',
  manage: 'manage',
  switch: 'switch',
  services: 'services',
  venues: 'venues',
  equipment: 'equipment',
  // Admin menu types
  features: 'features',
  productCatalog: 'productCatalog',
  subscriptionPlans: 'subscriptionPlans',
  commercialFeatures: 'commercialFeatures',
  entitlementsSupport: 'entitlementsSupport',
  swagger: 'swagger',
  slideTemplates: 'slideTemplates',
  compositeComponents: 'compositeComponents',
  htmlComponents: 'htmlComponents',
  vmAdmin: 'vmAdmin',
  opsAdmin: 'opsAdmin',
  agentPools: 'agentPools',
} as const;

type MenuType = (typeof MENU_TYPE)[keyof typeof MENU_TYPE];

export type ListElement = {
  text: string;
  icon: ReactNode;
  type: MenuType;
  path?: string;
  count?: number;
};

type ActiveOrganizationMenuProps = {
  onMenuItemClick?: () => void;
  onModalOpen?: () => void;
  onModalClose?: () => void;
  isModalOpen?: boolean;
};

export default function ActiveOrganizationMenu({
  onMenuItemClick,
  onModalOpen,
  onModalClose,
  isModalOpen,
}: ActiveOrganizationMenuProps) {
  const t = useTranslations();
  const location = useLocation();

  const [menuSelected, setMenuSelected] = useState<MenuType | undefined>(undefined);

  const { setActiveOrganization, activeOrganization } = useActiveOrganizationStore();
  const navigate = useNavigate();
  const { data: permissions } = usePermissionsQuery();
  const { data: getOrganizationData, isSuccess: getOrganizationIsSuccess } = useGetOrganizationQuery(
    activeOrganization?.id,
  );

  // Sync menu selection with pathname during render
  const [prevPathname, setPrevPathname] = useState(location.pathname);
  if (prevPathname !== location.pathname) {
    setPrevPathname(location.pathname);
    if (location.pathname.includes('/events')) {
      setMenuSelected(MENU_TYPE.events);
    } else if (location.pathname.includes('/team')) {
      setMenuSelected(MENU_TYPE.team);
    } else if (location.pathname.includes('/services')) {
      setMenuSelected(MENU_TYPE.services);
    } else if (location.pathname.includes('/venues')) {
      setMenuSelected(MENU_TYPE.venues);
    } else if (location.pathname.includes('/equipment')) {
      setMenuSelected(MENU_TYPE.equipment);
    } else if (menuSelected !== 'manage' && menuSelected !== 'switch') {
      setMenuSelected(undefined);
    }
  }

  useEffect(() => {
    if (menuSelected === 'manage' || menuSelected === 'switch') {
      if (!isModalOpen) onModalOpen?.();
    }
  }, [menuSelected, isModalOpen, onModalOpen]);

  useEffect(() => {
    // Only refresh the store with fetched details for the *current* active org.
    // Applying a stale response for a previous org after a switch blanks org routes
    // (URL already points at the new org, store would snap back).
    if (getOrganizationData && getOrganizationIsSuccess && getOrganizationData.id === activeOrganization?.id) {
      setActiveOrganization(getOrganizationData);
    }
  }, [getOrganizationIsSuccess, getOrganizationData, activeOrganization?.id, setActiveOrganization]);

  const handleModalClick = (type: MenuType) => setMenuSelected(type);

  const handleModalClose = () => {
    setMenuSelected(undefined);
    onMenuItemClick?.();
  };

  const menuButtons: (ListElement | typeof Divider)[] = useMemo(
    () => [
      {
        icon: <FestivalOutlined fontSize="inherit" />,
        text: t('common.events'),
        type: MENU_TYPE.events,
        path: `/organization/${activeOrganization?.id}/events`,
      },
      {
        icon: <GroupsOutlined fontSize="inherit" />,
        text: t('common.team'),
        type: MENU_TYPE.team,
        path: `/organization/${activeOrganization?.id}/team`,
      },
    ],
    [t, activeOrganization?.id],
  );

  const orgForPlan = (getOrganizationData ?? activeOrganization) as OrganizationDto | undefined;
  const planButtonRows = useMemo((): BookedMainPlanSummaryEntry[] => {
    const fromSubs = bookedMainSubscriptionSummaryEntries(orgForPlan);
    if (fromSubs.length > 0) {
      return fromSubs;
    }
    const primary = getPrimaryOrganizationSubscriptionInstance(orgForPlan);
    const plan = primary?.subscriptionPlan;
    if (!primary || !plan) {
      return [];
    }
    const pk = (primary.subscriptionProductKey?.trim() || normalizeSubscriptionProductKey(plan)) as string;
    return [
      {
        subscriptionProductKey: pk,
        category: subscriptionCategoryKey(plan),
        tier: (plan.tier ?? primary.tier ?? 'FREE') as string,
      },
    ];
  }, [orgForPlan]);

  if (!activeOrganization) {
    return null;
  }

  const orgPermSet = permissions?.organizations?.[activeOrganization.id];
  const canUpdatePlan =
    !!orgPermSet?.includes(ORGANIZATION_PERMISSIONS.MANAGE_SUBSCRIPTION_CART) ||
    !!orgPermSet?.includes(ORGANIZATION_PERMISSIONS.COMPLETE_SUBSCRIPTION_CHECKOUT) ||
    !!orgPermSet?.includes(ORGANIZATION_PERMISSIONS.UPDATE_SUBSCRIPTION_PLAN);

  const openPlanPageForCategory = (category: SubscriptionCategory) => {
    if (!activeOrganization?.id) {
      return;
    }
    const q = new URLSearchParams({ category });
    navigate(`/organization/${activeOrganization.id}/plan?${q.toString()}`);
    onMenuItemClick?.();
    onModalClose?.();
  };

  return (
    <>
      <Card
        sx={{
          width: 250,
          boxShadow: 'none',
          border: `1px solid`,
          borderColor: 'divider',
          borderRadius: 2,
          height: 'auto',
        }}
      >
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', gap: 1 }}>
              <BusinessOutlined
                sx={{
                  fontSize: '1.0rem',
                  color: 'text.primary',
                  flexShrink: 0,
                  mt: 0.25,
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography
                  variant="body2"
                  sx={{
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    textOverflow: 'ellipsis',
                    width: '100%',
                    color: 'text.primary',
                    fontWeight: 500,
                  }}
                >
                  {activeOrganization.name}
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    gap: 0.75,
                    alignItems: 'center',
                    width: '100%',
                    minHeight: 28,
                  }}
                >
                  {planButtonRows.map((row) => {
                    const productName =
                      resolveTranslatedProductField(
                        t,
                        'subscription.organization.planPage.productName',
                        row.subscriptionProductKey,
                      ) ??
                      t(
                        `subscription.organization.planPage.categories.${row.category}` as 'subscription.organization.planPage.categories.HOST',
                      );
                    const tierLabel = t(
                      `subscription.organization.tiers.${row.tier}` as 'subscription.organization.tiers.FREE',
                    );
                    const planButtonLabel = `${productName} | ${tierLabel}`;
                    return (
                      <Button
                        key={`plan-btn-${row.subscriptionProductKey}-${row.tier}`}
                        type="button"
                        size="small"
                        variant="outlined"
                        color="inherit"
                        disabled={!canUpdatePlan}
                        onClick={() => openPlanPageForCategory(row.category)}
                        startIcon={<EditOutlined sx={{ fontSize: '0.95rem !important' }} />}
                        sx={{
                          'flexShrink': 0,
                          'py': 0.25,
                          'px': 0.75,
                          'minWidth': 0,
                          'fontSize': '0.7rem',
                          'lineHeight': 1.25,
                          'textTransform': 'none',
                          'borderColor': 'divider',
                          'color': 'text.secondary',
                          '& .MuiButton-startIcon': { mr: 0.35, ml: -0.25 },
                        }}
                        aria-label={t('subscription.organization.planPage.openPlanPageForSubscriptionAria' as const, {
                          label: planButtonLabel,
                        })}
                      >
                        {planButtonLabel}
                      </Button>
                    );
                  })}
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 0.5,
                    justifyContent: 'flex-end',
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => handleModalClick(MENU_TYPE.manage)}
                    sx={{
                      padding: 0.5,
                    }}
                    aria-label={t('common.manage')}
                  >
                    <SettingsOutlined fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleModalClick(MENU_TYPE.switch)}
                    sx={{
                      padding: 0.5,
                    }}
                    aria-label={t('common.switch')}
                  >
                    <SwapHorizOutlined fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          }
          sx={{
            'pl': 1,
            'pr': 2,
            'pt': 1.5,
            'pb': 1,
            'borderRadius': 0,
            'backgroundColor': (theme) => alpha(theme.palette.primary.main, 0.2),
            '& .MuiCardHeader-content': {
              width: '100%',
              minWidth: 0,
            },
          }}
        />

        <CardContent
          sx={{
            'pt': 0.5,
            '&.MuiCardContent-root': {
              paddingBottom: '8px',
            },
            'pl': 2,
            'pr': 1,
            'bgcolor': 'background.paper',
            'textAlign': 'left',
          }}
        >
          <List sx={{ py: 0, ml: -1 }}>
            {menuButtons.map((e, i) => {
              if (e === Divider) return <Divider key={`divider-${i}`} />;

              const listElement = e as ListElement;

              return (
                <Fragment key={`list-button-${i}`}>
                  {listElement.path ? (
                    <NavLink
                      to={listElement.path}
                      style={{
                        textDecoration: 'none',
                        color: 'inherit',
                      }}
                    >
                      <ActiveOrganizationMenuItem
                        listElement={listElement}
                        isSelected={
                          location.pathname === listElement.path || location.pathname.startsWith(listElement.path + '/')
                        }
                        onClick={() => {
                          setMenuSelected(listElement.type);
                          onMenuItemClick?.();
                        }}
                      />
                    </NavLink>
                  ) : (
                    <ActiveOrganizationMenuItem
                      listElement={listElement}
                      isSelected={menuSelected === listElement.type}
                      onClick={() => {
                        setMenuSelected(listElement.type);
                        onMenuItemClick?.();
                      }}
                    />
                  )}
                </Fragment>
              );
            })}
          </List>
        </CardContent>
      </Card>

      <EditActiveOrganization open={menuSelected === MENU_TYPE.manage} close={handleModalClose} />
      <UserOrganizations open={menuSelected === MENU_TYPE.switch} close={handleModalClose} />
    </>
  );
}
